import type {ProspectWorkflowSnapshot,ProspectCompany,ProspectWork} from './prospecting'

export function groupCompanyWork(snapshot:ProspectWorkflowSnapshot|undefined){
 const groups=new Map<string,{key:string;company?:ProspectCompany;work:ProspectWork[]}>()
 const prospectCompanies=new Map(snapshot?.prospects?.map(prospect=>[prospect.id,prospect.organizationId])??[])
 const contactCompanies=new Map<string,Set<string>>()
 for(const company of snapshot?.companies??[])for(const contact of canonicalCompanyContacts(company)){
  const memberships=contactCompanies.get(contact.id)??new Set<string>();memberships.add(company.id);contactCompanies.set(contact.id,memberships)
 }
 for(const work of snapshot?.work??[]){
  if(work.status!=='OPEN')continue
  const prospectCompany=prospectCompanies.get(work.prospectId),contactCompanyIds=work.contactId?contactCompanies.get(work.contactId):undefined
  const contradictory=Boolean(work.organizationId&&((prospectCompany&&prospectCompany!==work.organizationId)||[...(contactCompanyIds??[])].some(companyId=>companyId!==work.organizationId)))
  // Unresolved identities are never combined by display name or domain guesses.
  // Contradictory foreign keys are a review projection, never a new company binding.
  const key=contradictory?'unresolved:'+work.prospectId:work.organizationId??'unresolved:'+work.prospectId
  let group=groups.get(key)
  if(!group){group={key,company:contradictory?undefined:snapshot?.companies?.find(company=>company.id===work.organizationId),work:[]};groups.set(key,group)}
  group.work.push(work)
 }
 for(const company of snapshot?.companies??[]){if(!groups.has(company.id))groups.set(company.id,{key:company.id,company,work:[]})}
 return [...groups.values()]
}

/** The server supplies the default queue; task grouping must not override its order. */
export function companyClaimQueueGroups(snapshot:ProspectWorkflowSnapshot|undefined){
 const groups=groupCompanyWork(snapshot),ids=snapshot?.claimQueueCompanyIds,known=new Set(snapshot?.companies?.map(company=>company.id)??[])
 const ready=Array.isArray(ids)&&new Set(ids).size===ids.length&&ids.every(id=>typeof id==='string'&&known.has(id))
 const rank=new Map((ready?ids:[]).map((id,index)=>[id,index]))
 return {ready,companies:ready?groups.filter(group=>group.company&&rank.has(group.company.id)).sort((a,b)=>rank.get(a.company!.id)!-rank.get(b.company!.id)!):[],
  retained:groups.filter(group=>group.company&&!rank.has(group.company.id)&&group.work.length>0),unresolved:groups.filter(group=>!group.company)}
}

export function verifiedCompanyClaim(value:unknown,companyId:string,actorId:string,expectedVersion?:number){
 if(!value||typeof value!=='object')return false
 const receipt=value as Record<string,unknown>
 return receipt.companyId===companyId&&receipt.ownerUserId===actorId&&Number.isSafeInteger(receipt.claimVersion)&&Number(receipt.claimVersion)>0&&(expectedVersion===undefined||Number(receipt.claimVersion)===expectedVersion+1||(receipt.duplicate===true&&Number(receipt.claimVersion)===expectedVersion))
}

export function companyCanClaim(company:ProspectCompany){return company.canClaim===true}

export function canonicalCompanyContacts(company:ProspectCompany){
 // A missing relationship is not proof that a contact belongs to this company.
 return (company.contacts??[]).filter(contact=>contact.organizationId===company.id)
}

export function companyAssignmentLabel(company:ProspectCompany,actorId:string){
 const owner=company.ownerDisplayName||company.ownerUserId||'Unknown user'
 if(company.ownershipState==='ADMIN_DEFAULT_UNCLAIMED'&&companyCanClaim(company))return 'Unclaimed · available to claim'
 if(!company.ownerUserId)return companyCanClaim(company)?'Unassigned · available to claim':'Unassigned · claim unavailable'
 if(company.ownershipState==='REP_CLAIMED')return company.ownerUserId===actorId?'Claimed by you':`Claimed by ${owner}`
 if(company.ownerUserId===actorId&&companyCanClaim(company))return 'Existing ownership is yours · rep claim not recorded'
 return `Rep claim not recorded · existing owner: ${owner}. Ownership review required.`
}
